/**
 * usePresenceBeat - tell the backend this fan is here, and roughly where.
 *
 * Mounted ONCE, at the app shell, so it covers every page. The messaging
 * websocket cannot do this job: it only exists inside MessagingProvider, which
 * mounts on /messages, so a fan in the gallery has no socket at all.
 *
 * DESIGN RULES, in order of importance:
 *
 *  1. It must never break anything. Every failure is swallowed. A dead
 *     presence endpoint costs a coloured ring in the admin dashboard and
 *     nothing else - no error surfaces to the fan, no retry storm, no state.
 *
 *  2. It must never be the reason a request is rate limited. The beat is
 *     exempt from the gateway limiter, it fires at 25s, and it stops entirely
 *     while the tab is hidden.
 *
 *  3. It must not lie. A hidden tab is not "in the chat", so a backgrounded
 *     tab stops beating and the fan ages out after the server-side TTL.
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
const BEAT_MS = 25000

/** First path segment, which is all the server needs to colour the ring. */
function pageFromPath(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)[0]
  return seg ? seg.toLowerCase() : 'home'
}

export function usePresenceBeat(isAuthenticated: boolean) {
  const location = useLocation()
  const pageRef = useRef(pageFromPath(location.pathname))

  // Keep the page current without restarting the interval on every navigation.
  // Restarting it would fire an extra request per route change, which is
  // exactly the pattern that caused the admin polling storm.
  pageRef.current = pageFromPath(location.pathname)

  useEffect(() => {
    if (!isAuthenticated) return

    let cancelled = false

    const send = (path: 'beat' | 'leave') => {
      const token = localStorage.getItem('auth_token')
      if (!token || cancelled) return

      // keepalive lets the final beat survive the page unloading.
      fetch(`${API_URL}/api/messages/presence/${path}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: pageRef.current }),
        keepalive: true
      }).catch(() => {
        // Deliberately silent. Presence is decoration; it must never surface
        // an error or trigger a retry.
      })
    }

    // A hidden tab is not presence. Stop beating, and let the server TTL
    // expire the fan naturally rather than reporting them as active.
    let timer: ReturnType<typeof setInterval> | null = null

    const start = () => {
      if (timer) return
      send('beat')
      timer = setInterval(() => send('beat'), BEAT_MS)
    }

    const stop = () => {
      if (!timer) return
      clearInterval(timer)
      timer = null
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') start()
      else stop()
    }

    if (document.visibilityState === 'visible') start()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [isAuthenticated])
}
